/**
 * AnalyticsSection — V3 charts overlay for the dashboard.
 * Fetches from /api/v3/dashboard/analytics and /kpis.
 * Renders ECharts: revenue trend, orders by site, pipeline, margin distribution.
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { useIsMobile } from '../../hooks/useIsMobile';
import { AreaChart } from '../../components/charts/AreaChart';
import { BarChart } from '../../components/charts/BarChart';
import { PieChart } from '../../components/charts/PieChart';
import {
  TrendingUp, TrendingDown, Minus, DollarSign, ShoppingCart,
  Package, AlertTriangle, BarChart3, Loader2
} from 'lucide-react';
import { Can } from '../../lib/rbac';

const SITE_COLORS: Record<string, string> = {
  MLA: '#6366f1', MLM: '#22c55e', MLC: '#f59e0b', MCO: '#ef4444',
  MLU: '#8b5cf6', MLB: '#06b6d4', unknown: '#6b7280',
};
const SITE_FLAGS: Record<string, string> = {
  MLA: '\u{1F1E6}\u{1F1F7}', MLM: '\u{1F1F2}\u{1F1FD}', MLC: '\u{1F1E8}\u{1F1F1}',
  MCO: '\u{1F1E8}\u{1F1F4}', MLU: '\u{1F1FA}\u{1F1FE}', MLB: '\u{1F1E7}\u{1F1F7}',
};

function fmt$(n: number | null | undefined): string {
  if (n == null) return '$0';
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

function KpiCard({ title, value, prev, icon, format = 'number' }: {
  title: string; value: number; prev?: number; icon: React.ReactNode; format?: 'currency' | 'number' | 'percent';
}) {
  const formatted = format === 'currency' ? fmt$(value) : format === 'percent' ? `${value.toFixed(1)}%` : String(value);
  const change = prev && prev > 0 ? ((value - prev) / prev) * 100 : null;
  const TrendIcon = change && change > 0 ? TrendingUp : change && change < 0 ? TrendingDown : Minus;
  const trendColor = change && change > 0 ? 'text-green-400' : change && change < 0 ? 'text-red-400' : 'text-gray-500';

  return (
    <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-[var(--color-text-muted)] uppercase tracking-wider font-medium">{title}</span>
        <span className="text-[var(--color-text-muted)]">{icon}</span>
      </div>
      <div className="flex items-end gap-2">
        <span className="text-2xl font-bold text-[var(--color-text)]">{formatted}</span>
        {change !== null && (
          <span className={`flex items-center gap-0.5 text-xs font-medium mb-0.5 ${trendColor}`}>
            <TrendIcon size={12} />
            {change > 0 ? '+' : ''}{change.toFixed(1)}%
          </span>
        )}
      </div>
    </div>
  );
}

export function AnalyticsSection() {
  const { sellerId } = useUIStore();
  const isMobile = useIsMobile();
  const [period, setPeriod] = useState('7d');
  const [kpis, setKpis] = useState<any>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const days = period === '1d' ? 1 : period === '30d' ? 30 : 7;

  const load = useCallback(async () => {
    setLoading(true);
    const [kpiRes, analyticsRes] = await Promise.all([
      api.get<any>(`/v3/dashboard/kpis?seller_id=${sellerId}&period=${period}`),
      api.get<any>(`/v3/dashboard/analytics?seller_id=${sellerId}&days=${days}`),
    ]);
    if (kpiRes.success && kpiRes.data) setKpis(kpiRes.data);
    if (analyticsRes.success && analyticsRes.data) setAnalytics(analyticsRes.data);
    setLoading(false);
  }, [sellerId, period, days]);

  useEffect(() => { load(); }, [load]);

  if (loading && !kpis) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--color-primary)]" />
      </div>
    );
  }

  const cur = kpis?.current || {};
  const prev = kpis?.previous || {};

  // Format revenue trend dates
  const revenueTrend = (analytics?.revenue_trend || []).map((d: any) => ({
    ...d,
    date: new Date(d.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
  }));

  // Orders by site for bar chart
  const siteData = (analytics?.orders_by_site || []).map((s: any) => ({
    label: `${SITE_FLAGS[s.site] || ''} ${s.site}`,
    value: s.revenue,
    color: SITE_COLORS[s.site] || SITE_COLORS.unknown,
  }));

  // Pipeline for pie chart
  const pipelineMap: Record<string, number> = {};
  for (const p of (analytics?.pipeline || [])) pipelineMap[p.status] = p.count;
  const pipelineData = [
    { name: 'Active', value: pipelineMap['active_cbt'] || 0, color: '#22c55e' },
    { name: 'Paused', value: pipelineMap['paused_cbt'] || 0, color: '#f59e0b' },
    { name: 'Under Review', value: pipelineMap['under_review'] || 0, color: '#ef4444' },
    { name: 'Queue (pending)', value: pipelineMap['pending'] || 0, color: '#6366f1' },
    { name: 'Draft', value: pipelineMap['draft'] || 0, color: '#8b5cf6' },
    { name: 'Published', value: pipelineMap['published'] || 0, color: '#14b8a6' },
  ].filter(d => d.value > 0);

  // Margin distribution for bar
  const marginData = (analytics?.margin_distribution || []).map((m: any) => ({
    label: m.bucket,
    value: m.count,
    color: m.bucket === 'negative' ? '#ef4444' : m.bucket === '30%+' ? '#22c55e' : '#6366f1',
  }));

  const chartHeight = isMobile ? 200 : 280;
  const barChartHeight = isMobile ? 200 : 260;

  return (
    <div className="space-y-4">
      {/* Period selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 size={20} className="text-[var(--color-primary)]" />
          <h2 className="text-base sm:text-lg font-semibold text-[var(--color-text)]">Analytics</h2>
        </div>
        <div className="flex gap-1 bg-[var(--color-bg-secondary)] p-0.5 rounded-lg">
          {['1d', '7d', '30d'].map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                period === p ? 'bg-[var(--color-primary)] text-white' : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text)]'
              }`}>
              {p === '1d' ? 'Hoy' : p === '7d' ? '7 días' : '30 días'}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Can cap="global.view_money">
          <KpiCard title="Revenue" value={cur.revenue || 0} prev={prev.revenue} icon={<DollarSign size={16} />} format="currency" />
        </Can>
        <KpiCard title="Orders" value={cur.orders || 0} prev={prev.orders} icon={<ShoppingCart size={16} />} />
        <Can cap="global.view_money">
          <KpiCard title="Avg Margin" value={cur.avg_margin || 0} icon={<TrendingUp size={16} />} format="percent" />
        </Can>
        <KpiCard title="Active Listings" value={cur.active_listings || 0} icon={<Package size={16} />} />
        <KpiCard title="Open Claims" value={cur.open_claims || 0} icon={<AlertTriangle size={16} />} />
        <KpiCard title="Queue" value={cur.queue_pending || 0} icon={<Package size={16} />} />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Revenue Trend */}
        <Can cap="global.view_money">
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4">
            <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-3">Revenue Trend</h3>
            {revenueTrend.length > 0 ? (
              <AreaChart
                data={revenueTrend}
                series={[{ key: 'revenue', name: 'Revenue', color: '#6366f1' }]}
                height={chartHeight}
                formatTooltip={(v) => fmt$(v)}
              />
            ) : (
              <div className="h-[200px] sm:h-[280px] flex items-center justify-center text-[var(--color-text-tertiary)] text-sm">No data for this period</div>
            )}
          </div>
        </Can>

        {/* Orders by Site */}
        <Can cap="global.view_money">
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4">
            <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-3">Revenue por Site</h3>
            {siteData.length > 0 ? (
              <BarChart data={siteData} height={barChartHeight} formatValue={(v) => fmt$(v)} />
            ) : (
              <div className="h-[200px] sm:h-[260px] flex items-center justify-center text-[var(--color-text-tertiary)] text-sm">No data</div>
            )}
          </div>
        </Can>

        {/* Pipeline */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4">
          <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-3">Product Pipeline</h3>
          {pipelineData.length > 0 ? (
            <PieChart data={pipelineData} height={barChartHeight} />
          ) : (
            <div className="h-[200px] sm:h-[260px] flex items-center justify-center text-[var(--color-text-tertiary)] text-sm">No data</div>
          )}
        </div>

        {/* Margin Distribution */}
        <Can cap="global.view_money">
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4">
            <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-3">Distribución de Margen</h3>
            {marginData.length > 0 ? (
              <BarChart data={marginData} height={barChartHeight} formatValue={(v) => String(v)} barColor="#8b5cf6" />
            ) : (
              <div className="h-[200px] sm:h-[260px] flex items-center justify-center text-[var(--color-text-tertiary)] text-sm">No data</div>
            )}
          </div>
        </Can>
      </div>

      {/* Top Products */}
      {(analytics?.top_products || []).length > 0 && (
        <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4">
          <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-3">Top Products (Revenue)</h3>
          <div className="space-y-2">
            {analytics.top_products.slice(0, 5).map((p: any, i: number) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-xs text-[var(--color-text-tertiary)] w-5 text-right font-medium">#{i + 1}</span>
                {p.thumbnail ? (
                  <img src={p.thumbnail} alt="" className="w-8 h-8 rounded object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded bg-[var(--color-bg-tertiary)]" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[var(--color-text)] truncate">{p.title || p.asin}</p>
                  <p className="text-xs text-[var(--color-text-tertiary)]">{p.sales} sales</p>
                </div>
                <Can
                  cap="global.view_money"
                  fallback={<span className="text-sm text-[var(--color-text-muted)]">—</span>}
                >
                  <span className="font-mono text-sm font-bold text-green-400">{fmt$(p.revenue)}</span>
                </Can>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
